import { useState, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MetricCard } from '@/components/ui/metric-card';
import { CTABanner } from '@/components/ui/cta-banner';
import { Spinner } from '@/components/ui/spinner';
import { OmniSearch } from '@/components/layout';
import { useDIDMetrics, usePortOrders, useCDRTrend, useCDRDailyTrend, useTelviaIQOrders } from '@/features/dids';
import type { CDRTrendItem } from '@/features/dids';
import type { CDRDailyTrendItem } from '@/features/dids/api';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useClickOutside } from '@/hooks';
import {
  Phone,
  Shield,
  ArrowLeftRight,
  FileText,
  Zap,
  CircleAlert,
  CheckCircle2,
  Clock,
  ChevronDown,
  LogOut,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type TimeRange = 'daily' | '3m' | '6m' | '12m';

// Get time-based greeting
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('daily');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();

  useClickOutside(menuRef, () => setIsUserMenuOpen(false));

  // Get user initials from first/last name, or fallback to email
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : user?.firstName
    ? user.firstName.slice(0, 2).toUpperCase()
    : user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : 'AD';

  const { data: metrics, isLoading: metricsLoading } = useDIDMetrics();
  const { data: portOrders = [], isLoading: portOrdersLoading } = usePortOrders();
  const { data: trendData = [], isLoading: trendLoading } = useCDRTrend(12);
  const { data: dailyTrendData = [], isLoading: dailyTrendLoading } = useCDRDailyTrend(7);
  const { data: activityLog = [], isLoading: activityLoading } = useTelviaIQOrders({ limit: 5 });

  // Compute port order counts
  const activePortOrders = useMemo(() => {
    return portOrders.filter(p =>
      p.status === 'NEW' ||
      p.status === 'PENDING' ||
      p.status === 'SUBMITTED' ||
      p.status === 'FOCd'
    ).length;
  }, [portOrders]);

  // Format large numbers (e.g., 4200000 -> "4.2M")
  const formatLargeNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  // Transform CDR trend data for chart based on selected time range
  const chartData = useMemo(() => {
    if (timeRange === 'daily') {
      if (!dailyTrendData || dailyTrendData.length === 0) return [];
      return dailyTrendData.map((item: CDRDailyTrendItem) => ({
        time: item.day_name,
        minutes: Math.round(item.orig_minutes + item.term_minutes),
        inbound: Math.round(item.orig_minutes),
        outbound: Math.round(item.term_minutes),
      }));
    }

    if (!trendData || trendData.length === 0) return [];

    // Determine how many months to show based on time range
    const monthsToShow = timeRange === '3m' ? 3 : timeRange === '6m' ? 6 : 12;

    // Get the most recent N months of data
    const recentData = trendData.slice(-monthsToShow);

    return recentData.map((item: CDRTrendItem) => ({
      time: item.month_name.substring(0, 3), // Abbreviate month name (e.g., "January" -> "Jan")
      minutes: Math.round(item.orig_minutes + item.term_minutes),
      inbound: Math.round(item.orig_minutes),
      outbound: Math.round(item.term_minutes),
    }));
  }, [trendData, dailyTrendData, timeRange]);

  // Get activity icon based on order type
  const getActivityIcon = (orderType: string, status: string) => {
    if (status === 'FAILED') {
      return <CircleAlert size={16} className="text-rose-500" />;
    }
    if (orderType.includes('E911')) {
      return <CircleAlert size={16} className="text-amber-500" />;
    }
    if (orderType.includes('PORT')) {
      return <CheckCircle2 size={16} className="text-cyan-500" />;
    }
    return <Clock size={16} className="text-slate-400" />;
  };

  // Format relative time
  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  const isLoading = metricsLoading || portOrdersLoading;

  // Derive display name from user - use firstName if available, fallback to username
  const displayName = user?.firstName || user?.email?.split('@')[0] || 'there';

  // Profile dropdown name - use "First Last" or "First" or username
  const profileDisplayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName
    ? user.firstName
    : user?.email?.split('@')[0] || 'User';

  // Hero header with utilities - extracted for reuse
  const renderHeroHeader = () => (
    <div className="sticky top-0 overflow-visible border-b border-slate-200 bg-white z-40">
      <div className="px-6 py-4">
        {/* Row 1: Greeting + Utilities */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">
            {getGreeting()}, {displayName}
          </h1>

          {/* Right side: Search + User */}
          <div className="flex items-center gap-4">
            {/* Omni-Search */}
            <OmniSearch />

            {/* User menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-3 pl-3 pr-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">
                    {profileDisplayName}
                  </p>
                  <p className="text-xs text-slate-500">Tier 3 Enterprise</p>
                </div>
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-sm font-bold text-white">
                  {userInitials}
                </div>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-fade-in z-50">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {profileDisplayName}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
                  </div>
                  <button
                    onClick={async () => {
                      setIsUserMenuOpen(false);
                      await logout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <LogOut size={16} strokeWidth={1.5} />
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Status line */}
        <div className="flex items-center gap-2 mt-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-sm text-slate-600">
            Your carrier infrastructure is <span className="font-medium text-emerald-600">100% operational</span>.
          </span>
        </div>
      </div>
      {/* Watermark Logo - positioned within content area to not affect z-index */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none -z-10">
        <img src="/logo-icon.png" alt="" className="w-64 h-64" />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {renderHeroHeader()}
        <div className="flex items-center justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {renderHeroHeader()}

      <div className="p-6 space-y-6">
        {/* Metric Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            icon={Phone}
            label="Total DIDs"
            value={(metrics?.total_dids ?? 0).toLocaleString()}
            onClick={() => navigate('/dids')}
          />
          <MetricCard
            icon={Shield}
            label="Registered E911"
            value={(metrics?.e911_registered ?? 0).toLocaleString()}
            onClick={() => navigate('/dids?view=911-compliance')}
          />
          <MetricCard
            icon={ArrowLeftRight}
            label="Porting Orders"
            value={activePortOrders}
            onClick={() => navigate('/porting')}
          />
          <MetricCard
            icon={FileText}
            label="Monthly Usage"
            value={formatLargeNumber(metrics?.total_minutes_mtd ?? 0)}
            onClick={() => navigate('/usage')}
          />
        </div>

        {/* Usage Pulse + Recent Activity Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Usage Pulse Chart - 2/3 width */}
          <div
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/usage')}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Usage Trend</h2>
                <p className="text-sm text-slate-500">
                  {timeRange === 'daily' ? 'Daily minute usage over the last 7 days' : 'Monthly minute usage over time'}
                </p>
              </div>
              <div className="flex bg-slate-100 rounded-lg p-1">
                {(['daily', '3m', '6m', '12m'] as TimeRange[]).map((range) => (
                  <button
                    key={range}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeRange(range);
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      timeRange === range
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {range === 'daily' ? 'Daily' : range}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-[280px]">
              {(timeRange === 'daily' ? dailyTrendLoading : trendLoading) ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorMinutes" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      }}
                      labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                      formatter={(value) => [
                        `${Number(value).toLocaleString()} mins`,
                        'Total',
                      ]}
                    />
                    <Area
                      type="monotone"
                      dataKey="minutes"
                      stroke="#6366F1"
                      strokeWidth={2}
                      fill="url(#colorMinutes)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Recent Activity Feed - 1/3 width */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Recent Activity</h2>

            <div className="space-y-4">
              {activityLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : activityLog.length > 0 ? (
                activityLog.slice(0, 4).map((activity) => (
                  <div key={activity.id} className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getActivityIcon(activity.order_type, activity.status)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-900 leading-tight">
                        {activity.order_type_display}
                        {activity.tn && (
                          <span className="font-mono-data text-indigo-600 ml-1">
                            {activity.tn.replace(/(\d{3})(\d{3})(\d{4})/, '+1 ($1) $2-$3')}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {formatRelativeTime(activity.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No recent activity
                </p>
              )}
            </div>

            <Link
              to="/order-history"
              className="block mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700 text-center"
            >
              View Full History
            </Link>
          </div>
        </div>

        {/* CTA Banner */}
        <CTABanner
          icon={Zap}
          title="Provision new Numbers instantly"
          description="Scale your infrastructure with zero-latency provisioning."
          buttonText="Order DIDs Now"
          onClick={() => navigate('/dids/search')}
        />
      </div>
    </div>
  );
}
